import ModuleContext from 'CMFW/context/ModuleContext';
import Grid from 'CMFW/grid/Grid';
import { ContainerLeft, ContainerLeftHeader, EditorRow, ModuleContainer } from 'components/common/styles';
import { useForceRender } from 'hooks/useForceRender';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, TextField, IconButton } from '@material-ui/core';
import { RowNode } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { IFields } from '../../../CMFW/grid/fields';
import MemoryPatternFrequencyConfig, { IPatternFrequencyConfig } from '../components/MemoryPatternFrequencyConfig';
import MemoryPatternFrequencyModel from '../../../models/MemoryPatternFrequencyModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

let pfFilterText = '';

const MemoryPatternFrequency: React.FC = () => {
  const forceRender = useForceRender();
  const [showResults, setShowResults] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  pfFilterText = filterText;

  // Debounce filter input to reduce filtering churn on large datasets
  useEffect(() => {
    const h = setTimeout(() => setFilterText(filterInput), 300);
    return () => clearTimeout(h);
  }, [filterInput]);

  const moduleModel = useMemo(() => new MemoryPatternFrequencyModel({ render: forceRender }), [forceRender]);

  const handleGenerate = async (config: IPatternFrequencyConfig) => {
    setIsProcessing(true);
    try {
      setShowResults(true);
      if (typeof config.dialect === 'number') {
        moduleModel.setDialectId(config.dialect);
      } else if (typeof config.dialect === 'string' && config.dialect) {
        // if string provided, just skip; backend resolves it. Actions rely on numeric id, so prefer numbers.
      }
      await moduleModel.loadWithConfig(config);
    } catch (e) {
      console.error('Error generating Pattern Frequency:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToConfig = () => setShowResults(false);

  const isFilterActive = () => pfFilterText.trim() !== '';

  const isRowVisible = (node: RowNode) => {
    if (!isFilterActive()) return true;
    const t = pfFilterText.trim();
    let re: RegExp | null = null;
    try {
      re = new RegExp(t, 'i');
    } catch {
      re = null;
    }
    const data: any = node.data || {};
    const pat = String(data.pattern || '');
    const idStr = String(data.id || '');
    const countStr = String(data.count || '');
    if (t === '') return true;
    if (idStr === t) return true;
    if (re) {
      return re.test(pat) || re.test(countStr);
    }
    // Fallback contains check if regex creation failed
    return pat.toLowerCase().includes(t.toLowerCase()) || countStr.includes(t);
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.moduleNavigation]: 'multiNavigationRenderer',
      [IFields.actions]: 'msuChangeRenderer',
    }),
    [],
  );

  return (
    <ModuleContainer>
      <ModuleContext.Provider value={moduleModel}>
        <ContainerLeft>
          <ConfigContainer style={{ display: showResults ? 'none' : 'block' }}>
            <MemoryPatternFrequencyConfig onGenerate={handleGenerate} />
          </ConfigContainer>
          <ResultsContainer style={{ display: showResults ? 'block' : 'none' }}>
            <ContainerLeftHeader>
              <EditorRowStart>
                <Button variant="outlined" size="small" onClick={handleBackToConfig} disabled={isProcessing}>
                  Back to Configuration
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!Array.isArray(moduleModel.gridData) || moduleModel.gridData.length === 0}
                  onClick={() => {
                    const rows = moduleModel.gridData as any[];
                    const header = ['id', 'pattern', 'count'];
                    const csvLines = [header.join(',')];
                    for (const row of rows) {
                      const values = [
                        String(row.id ?? ''),
                        String(row.pattern ?? ''),
                        String(row.count ?? ''),
                      ].map((v) =>
                        v.includes('"') || v.includes(',') || v.includes('\n')
                          ? '"' + v.replace(/"/g, '""') + '"'
                          : v,
                      );
                      csvLines.push(values.join(','));
                    }
                    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'pattern_frequency.csv';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  }}
                >
                  CSV
                </Button>
                <ResultsInfo>
                  {Array.isArray(moduleModel.gridData) ? moduleModel.gridData.length : 0} patterns
                </ResultsInfo>
                <FilterRow>
                  <TextFieldStyled
                    variant="outlined"
                    size="small"
                    placeholder="Filter by text / id"
                    value={filterInput}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterInput(e.target.value)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFilterInput('');
                      setFilterText('');
                    }}
                    disabled={!filterInput}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </IconButton>
                </FilterRow>
              </EditorRowStart>
            </ContainerLeftHeader>
            <GridContainer>
              <Grid cellRenderers={cellRenderers} isFilterActive={isFilterActive} isRowVisible={isRowVisible} />
            </GridContainer>
          </ResultsContainer>
        </ContainerLeft>
      </ModuleContext.Provider>
    </ModuleContainer>
  );
};

export default MemoryPatternFrequency;

const EditorRowStart = styled(EditorRow)`
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
`;

const ConfigContainer = styled.div`
  height: 100%;
  overflow: hidden;
`;

const ResultsContainer = styled.div`
  height: 100%;
  overflow: hidden;
`;

const GridContainer = styled.div`
  height: calc(100% - 60px);
  overflow: hidden;
`;

const ResultsInfo = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const TextFieldStyled = styled(TextField)`
  flex-grow: 1;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;
